import { Refine } from "@pankod/refine-core";
import routerProvider from "@pankod/refine-react-router-v6";

import {
  notificationProvider,
  Layout,
  ReadyPage,
  ErrorComponent,
  ConfigProvider,
} from "@pankod/refine-antd";
import "@pankod/refine-antd/dist/styles.min.css";
import dataProvider from "@pankod/refine-simple-rest";
import axios, { AxiosInstance, AxiosRequestConfig } from "@pankod/refine-simple-rest/node_modules/axios";
import { authProvider } from "./providers/auth-provider.provider";
import { Login } from "pages/login";
import { DashboardPage } from "pages/dashboard";
import { Sider } from "components/organisms/sider.component";

import './sass/main.scss';

import { API_URL, PERMISSIONS_KEY, TOKEN_KEY } from "constants/environment";
import { UserCreate, UserEdit, UserList, UserShow } from "pages/users";
import { accessControlProvider } from "providers/access-control.provider";
import { useState } from "react";
import moment from "moment-timezone";
import locale from "antd/lib/locale/es_ES";
import { IdcardFilled, SettingFilled, UserOutlined } from "@ant-design/icons";
import { CustomPage } from "pages/custom/custom-page";
import { MessageTypeCreate, MessageTypeEdit, MessageTypeList } from "pages/message-types";
import { MessageList } from "pages/messages/list";
import { MessageCreate } from "pages/messages";
import { MessageSubTypeCreate, MessageSubTypeEdit, MessageSubTypeList } from "pages/message-subtypes";

const axiosInstance: AxiosInstance = axios.create();

axiosInstance.interceptors.request.use((request: AxiosRequestConfig) => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (token) {
        if (request.headers) {
            request.headers["Authorization"] = `Bearer ${token}`;
        } else {
            request.headers = {
                Authorization: `Bearer ${token}`,
            };
        }
    }
    return request;
});

function App() {
  const lsPermissions = localStorage.getItem(PERMISSIONS_KEY);
  const [permissions, setPermissions] = useState(lsPermissions || '');
  moment.tz.setDefault('America/Mazatlan');
  
  return (
    <ConfigProvider locale={locale}>
      <Refine
      notificationProvider={notificationProvider}
      Layout={Layout}
      Sider={Sider}
      ReadyPage={ReadyPage}
      catchAll={<ErrorComponent />}
      routerProvider={{
        ...routerProvider,
        routes: [
          {
            element: <CustomPage />,
            path: '/custom'
          }
        ] as typeof routerProvider.routes,
      }}
      accessControlProvider={accessControlProvider(permissions)}
      dataProvider={dataProvider(API_URL, axiosInstance)}
      authProvider={authProvider(axiosInstance, setPermissions)}
      LoginPage={Login}
      DashboardPage={DashboardPage}
      reactQueryDevtoolConfig={false}
      resources={[
        {
          name: "messages",
          icon: <SettingFilled />,
          options: {
            label:"Mensajes",
            route: "messages",
          }
        },
        {
          parentName: "messages",
          name: "general",
          icon: <IdcardFilled />,
          create: MessageCreate,
          list: MessageList,
          edit: MessageTypeEdit,
          options: {
            label: "Generales",
            route: "general",
            message_type: "1"
          },
        },
        {
          parentName: "messages",
          name: "payments",
          icon: <IdcardFilled />,
          list: MessageList,
          options: {
            label: "Recordatorios y Cobranza",
            route: "payments",
            message_type: "2",
            message_subtype: "1,3",
          }
        },
        {
          parentName: "messages",
          name: "payments-thanks",
          icon: <IdcardFilled />,
          list: MessageList,
          options: {
            label: "Agradecimientos de Pago",
            route: "payments-thanks",
            message_type: "2",
            message_subtype: "2",
          }
        },
        {
          name: "cruds",
          icon: <SettingFilled />,
          options: {
            label:"Configuración",
          }
        },
        {
          parentName: "cruds",
          name: "message-types",
          icon: <IdcardFilled />,
          create: MessageTypeCreate,
          list: MessageTypeList,
          edit: MessageTypeEdit,
          options: {
            label: "Tipos de Mensaje",
            route: "message-types"
          }
        },
        {
          parentName: "cruds",
          name: "message-subtypes",
          icon: <IdcardFilled />,
          create: MessageSubTypeCreate,
          list: MessageSubTypeList,
          edit: MessageSubTypeEdit,
          options: {
            label: "Subtipos de Mensaje",
            route: "message-subtypes"
          }
        },
        {
          parentName: "cruds",
          name: "users",
          icon: <UserOutlined />,
          create: UserCreate,
          list: UserList,
          show: UserShow,
          edit: UserEdit,
          options: {
            label: "Usuarios",
            route: "users"
          },
        }
      ]}
    />
    </ConfigProvider>
  );
}

export default App;
